"use client";

import { FC, useEffect, useRef, useState, useMemo } from "react";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/OnboardingWelcomeModal/OnboardingWelcomeModalContent";
import {
  OnboardingWelcomeCta,
  OnboardingWelcomeHeadline,
  OnboardingWelcomeWaveText,
} from "./OnboardingWelcomeAnimatedParts";
import { ONBOARDING_WELCOME_ANIM } from "./animationConstants";
import { useOnboardingWelcomeGsap } from "./useOnboardingWelcomeGsap";
import { getRandomTemplate } from "./templateLibrary";
import type { OnboardingWelcomeModalContentProps } from "./types";

const I18n = I18nUtil.createScoped(Translations);

/**
 * Onboarding Welcome — DOM 结构 + GSAP 时间轴对齐 HTML 演示稿
 */
export const OnboardingWelcomeModalContent: FC<
  OnboardingWelcomeModalContentProps
> = ({ userName = "Alex", itemCount = 5, onClose }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const [playGsap, setPlayGsap] = useState(false);

  const template = useMemo(
    () => getRandomTemplate(userName, itemCount),
    [userName, itemCount],
  );

  useOnboardingWelcomeGsap(contentRef, ctaRef, itemCount, playGsap);

  useEffect(() => {
    let cancelled = false;
    let enterTimer = 0;
    let raf = 0;

    enterTimer = window.setTimeout(() => {
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => {
          if (cancelled) {
            return;
          }
          const start = performance.now();
          setPlayGsap(true);
          rootRef.current?.setAttribute(
            "data-animation-started-at",
            String(start),
          );
        });
      });
    }, ONBOARDING_WELCOME_ANIM.drawerEnterDelayMs);

    return () => {
      cancelled = true;
      window.clearTimeout(enterTimer);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="flex h-full w-full flex-col bg-background_solid_neutral_secondary pb-safe"
      data-testid="onboarding-welcome-modal"
      data-tracking-eventname="catchup_view"
      data-tracking-event-key-unread_count={String(itemCount)}
    >
      <div
        ref={contentRef}
        className="flex flex-1 flex-col items-center justify-center px-5"
      >
        <div className="flex w-full  flex-col items-center text-center">
          <OnboardingWelcomeWaveText
            className="body_medium_xl mb-3 text-content_tertiary"
            data-testid="onboarding-welcome-greeting"
          >
            <p className="mx-auto  truncate" title={userName}>
              {template.l1}
            </p>
          </OnboardingWelcomeWaveText>

          <OnboardingWelcomeHeadline
            prefix={template.l2Prefix}
            suffix={template.l2Suffix}
            quantifier={template.l2Quantifier}
          />

          <OnboardingWelcomeWaveText
            className="body_m mt-6  text-content_tertiary"
            data-testid="onboarding-welcome-subtitle"
          >
            {template.l3}
          </OnboardingWelcomeWaveText>
        </div>
      </div>

      <div className="shrink-0 px-5 pb-5 ">
        <OnboardingWelcomeCta
          ref={ctaRef}
          data-openreplay-label="onboarding_onboardingwelcome_open"
          data-testid="onboarding-welcome-cta"
          data-tracking-eventname="catchup_click_through"
          data-tracking-event-key-unread_count={String(itemCount)}
          className={cn(
            "button button_l md_button primary_button flex h-12 w-full items-center justify-center rounded-2",
          )}
          onClick={() => onClose("view")}
        >
          {I18n.t("Click to view")}
        </OnboardingWelcomeCta>
      </div>
    </div>
  );
};
